import { useCallback, useEffect, useState } from 'react'
import { buildFormData } from './service'
import { useErrorHandling } from '../hooks/useErrorHandling'

/**
 * All methods in this file execute code equivalent to
 * try {
 *   do_something()
 *   execute_on_success()
 * } catch (e) {
 *   execute_on_error()
 * } finally {
 *   execute_on_either_success_or_error()
 * }
 */

const useHttpCallback = ({ callback, setLoading = null, setResult = null, onSuccess = null, onError = null, onFinally = null }) => {
  const httpErrorHandledCallback = useErrorHandling(
    useCallback(async (...args) => {
      setLoading && setLoading(true)
      const result = await callback(...args)
      setResult && setResult(result)

      // onSuccess is technically not requried, as the success code can simply be part of the callback, but is added
      // to make the api clearer.
      onSuccess && onSuccess(result)

      // returns the promise with data just in case we need it somewhere
      return result
    }, [callback, onSuccess, setLoading, setResult]),
    useCallback(() => {
      setLoading && setLoading(false)
      onFinally && onFinally()
    }, [onFinally, setLoading]),
    useCallback((error) => {
      onError && onError(error)
    }, [onError])
  )

  return httpErrorHandledCallback
}

/**
 * Used for GET /collection/:id or GET /collection requests
 * Used to fetch a single or multiple objects.
 * You can pass initialQueryParams - which will be used the first time the callback method is called from the useEffect.
 * You can then pass a queryParams JSON object each time you call the returned fetchItems method.
 * initialStateValue - is the initial state value (undefined by default). It could be [] when fetching collections or {} when fetching objects.
 * 
 * Runs the callback in a useEffect once the current react component is rendered.
 * @param {Object}  
 * @returns [item[s], setItem[s], fetchItem[s]]
 */
const useFetch = ({ callback, setLoading, initialQueryParams, initialStateValue = [], fetchImmediatelly = true, onSuccess, onError, onFinally }) => {
  // items can also be a single item - depending on the endpoint we hit
  const [items, setItems] = useState(initialStateValue)

  const fetchItems = useErrorHandling(
    useCallback(async (queryParams) => {
      setLoading && setLoading(true)
      const allItems = await callback(queryParams)
      setItems(allItems)

      // onSuccess is technically not requried, as the success code can simply be part of the callback, but is added
      // to make the api clearer.
      onSuccess && onSuccess(allItems)

      // returns the promise with data just in case we need it somewhere
      return allItems
    }, [callback, onSuccess, setLoading]),
    useCallback(() => {
      setLoading && setLoading(false)
      onFinally && onFinally()
    }, [onFinally, setLoading]),
    useCallback((error) => {
      onError && onError(error)
    }, [onError])
  )

  useEffect(() => {
    if (fetchImmediatelly) {
      fetchItems(initialQueryParams || {})
    }
  }, [fetchImmediatelly, fetchItems, initialQueryParams])

  return [items, setItems, fetchItems]
}

/**
 * Used for GET /collection/:id or GET /collection requests
 * Used to fetch a single or multiple objects.
 * Check useFetch.
 * Note that for this method the onSuccess, onError and onFinally will be called on each fetch request.
 * 
 * Creates an interval once the current react component is rendered. The interval's repeat time is intervalTimeMs or 5000ms by default.
 * The interval can then be started and cleared at will.
 * @param {*} param0 
 * @returns { stateVar, setStateVar, fetchMethod, interval, setInterval }
 */
const useFetchWithInterval = ({ api, callback, fetchImmediatelly=true, setLoading, initialQueryParams, initialStateValue, intervalTimeMs, onSuccess, onError, onFinally }) => {
  const [interval, setInterval] = useState(null)
  const [stateVar, setStateVar, fetchMethod] = useFetch({
    callback,
    setLoading,
    initialQueryParams,
    initialStateValue,
    onSuccess,
    onError,
    onFinally,
    fetchImmediatelly
  })

  // On the first render of the component where this method is used, we create the inverval.
  // At this point the interval is not started and no request is made. We need to call
  // interval.start() - to start it and interval.clear() - to clear it.
  useEffect(() => {
    const newInterval = api.base.createInterval(
      (param) => fetchMethod(param),
      intervalTimeMs || 5000
    )

    setInterval(newInterval)
  }, [api.base, fetchMethod, intervalTimeMs])

  return {
    stateVar, setStateVar, fetchMethod,
    interval, setInterval
  }
}

const useCreate = ({ callback, setLoading, onSuccess, onError, onFinally }) => {
  const create = useErrorHandling(
    useCallback(async (...args) => {
      setLoading && setLoading(true)
      const result = await callback(...args)

      onSuccess && onSuccess(result)
      setLoading && setLoading(false)

      return result
    }, [setLoading, callback, onSuccess]), useCallback(async () => {
      onFinally && await onFinally()
      setLoading && setLoading(false)
    }, [onFinally, setLoading]),
    useCallback(async (error) => {
      onError && await onError(error)
    }, [onError])
  )

  return create
}

/**
 * The resulting method accepts an object with the formDataJson field and any other fields.
 * It turns the formDataJson into a real form data - formData.
 * 
 * { field1: 1, value1, ..., formDataJson } => callback({ field1: value 1, ..., formData })
 * 
 * @param {*} param0 
 * @returns 
 */
const usePostFormData = ({ callback, setLoading, onSuccess, onError, onFinally }) => {
  const postFormData = useErrorHandling(
    useCallback(async (paramsObject) => {
      setLoading && setLoading(true)

      const { formDataJson } = paramsObject
      const formData = buildFormData(formDataJson)

      const result = await callback({ ...paramsObject, formData })
      setLoading && setLoading(false)

      // onSuccess is technically not requried, as the success code can simply be part of the callback, but is added
      // to make the api clearer.
      onSuccess && onSuccess(result)

      return result
    }, [setLoading, callback, onSuccess]), useCallback(() => {
      setLoading && setLoading(false)
      onFinally && onFinally()
    }, [onFinally, setLoading]),
    useCallback((error) => {
      onError && onError(error)
    }, [onError])
  )

  return postFormData
}

const useUpdate = ({ callback, setLoading, onSuccess, onError, onFinally }) => {
  const updateModel = useErrorHandling(
    useCallback(async (paramsObject) => {
      setLoading && setLoading(true)
      const result = await callback(paramsObject)
      setLoading && setLoading(false)

      // onSuccess is technically not requried, as the success code can simply be part of the callback, but is added
      // to make the api clearer.
      onSuccess && onSuccess(result)

      return result
    }, [setLoading, callback, onSuccess]), useCallback(() => {
      setLoading && setLoading(false)
      onFinally && onFinally()
    }, [onFinally, setLoading]),
    useCallback((error) => {
      onError && onError(error)
    }, [onError])
  )

  return updateModel
}

const useDelete = ({ callback, setLoading, onSuccess, onError, onFinally }) => {
  const deleteModel = useErrorHandling(
    useCallback(async (...params) => {
      setLoading && setLoading(true)
      const result = await callback(...params)
      setLoading && setLoading(false)

      // onSuccess is technically not requried, as the success code can simply be part of the callback, but is added
      // to make the api clearer.
      onSuccess && onSuccess()

      return result
    }, [setLoading, callback, onSuccess]), useCallback(() => {
      setLoading && setLoading(false)
      onFinally && onFinally()
    }, [onFinally, setLoading]),
    useCallback((error) => {
      onError && onError(error)
    }, [onError])
  )

  return deleteModel
}

const serviceMethods = {
  useHttpCallback,
  useFetch,
  useFetchWithInterval,
  useUpdate,
  useDelete,
  useCreate,
  usePostFormData
}

export default serviceMethods
